import { Button } from '@ophelos/ui/components/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@ophelos/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@ophelos/ui/components/popover';
import { cn } from '@ophelos/ui/lib/utils';
import { Check, ChevronDown, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

export interface ComboboxGroup {
  label: string;
  /** `value` is what gets stored; `label` is only ever displayed. */
  options: { value: string; label: string }[];
}

interface MultiSelectComboboxProps {
  groups: ComboboxGroup[];
  value: string[];
  onChange: (value: string[]) => void;
  /** Selecting this option clears all others; selecting any other clears it (e.g. "None of these"). */
  exclusiveOption?: string;
  /** Disables both interactive surfaces — the popover trigger and the per-chip removal buttons. */
  disabled?: boolean;
}

/** Searchable, grouped multi-select. Options live under section headings; selections show as chips. */
export function MultiSelectCombobox({ groups, value, onChange, exclusiveOption, disabled }: MultiSelectComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const labelFor = (option: string) =>
    groups.flatMap((group) => group.options).find((entry) => entry.value === option)?.label ?? option;

  const toggle = (option: string) => {
    if (exclusiveOption && option === exclusiveOption) {
      onChange(value.includes(option) ? [] : [option]);
      return;
    }
    const base = exclusiveOption ? value.filter((v) => v !== exclusiveOption) : value;
    onChange(base.includes(option) ? base.filter((v) => v !== option) : [...base, option]);
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="input" disabled={disabled} className="border w-full justify-between font-normal flex items-center gap-2 border-input">
            <span className={cn('text-sm', value.length === 0 && 'text-muted-foreground')}>
              {value.length === 0
                ? t('budgetAssessment.situation.placeholder')
                : t('budgetAssessment.situation.selectedCount', { count: value.length })}
            </span>
            <ChevronDown className="size-5 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-(--radix-popover-trigger-width) p-0 rounded-xl" align="center">
          <Command>
            <CommandInput placeholder={t('budgetAssessment.situation.searchPlaceholder')} />
            <CommandList>
              <CommandEmpty>{t('budgetAssessment.situation.noMatches')}</CommandEmpty>
              {groups.map((group) => (
                <CommandGroup key={group.label} heading={group.label}>
                  {group.options.map((option) => (
                    // `value` drives Command's search, so it carries the label the customer reads.
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => toggle(option.value)}
                      className="cursor-pointer"
                    >
                      <Check className={cn('size-4', value.includes(option.value) ? 'opacity-100' : 'opacity-0')} />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((option) => (
            <button
              key={option}
              type="button"
              disabled={disabled}
              onClick={() => toggle(option)}
              aria-label={t('budgetAssessment.situation.remove', { option: labelFor(option) })}
              className="inline-flex items-center gap-1 rounded-full border border-ring highlight-accent px-3 py-1 text-xs"
            >
              {labelFor(option)}
              <X className="size-3" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
